/**
 * Personal task inbox — across all workspaces, all projects.
 *
 * FRIDAY-76: the view groups the tasks under their workspace and their
 * project, and it can filter and sort them. The server sends the project and
 * the workspace of every task, so the grouping needs no extra requests.
 */

import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { compareTaskKeys } from '@friday/shared/listFields'
import { PRIORITY_RANK } from '@/features/projects/sorting'
import type { Task } from '@friday/shared/realtime'

export interface MyWorkProject {
  id: string
  key: string
  name: string
  color: string
  workspaceId: string
}

export interface MyWorkWorkspace {
  id: string
  name: string
  slug: string
}

/** A task of the current user, with the project and the workspace it lives in. */
export interface MyWorkTask extends Task {
  project: MyWorkProject | null
  workspace: MyWorkWorkspace | null
}

export function useMyTasks(includeDone = false) {
  return useQuery({
    queryKey: ['my-tasks', includeDone],
    queryFn: () => {
      const qs = includeDone ? '?includeDone=true' : ''
      return api<MyWorkTask[]>(`/api/v1/me/tasks${qs}`)
    },
  })
}

/** The sorts the My Work view offers. */
export type MyWorkSortKey = 'due' | 'priority' | 'title' | 'key' | 'updated'

export const MY_WORK_SORT_OPTIONS: { value: MyWorkSortKey; label: string }[] = [
  { value: 'due', label: 'Due date' },
  { value: 'priority', label: 'Priority' },
  { value: 'title', label: 'Name' },
  { value: 'key', label: 'Key' },
  { value: 'updated', label: 'Last updated' },
]

/** The filters the My Work view offers. */
export type MyWorkStatus = 'all' | 'overdue' | 'week' | 'none'

export const MY_WORK_STATUS_OPTIONS: { value: MyWorkStatus; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'week', label: 'Due this week' },
  { value: 'none', label: 'No due date' },
]

const DAY = 24 * 60 * 60 * 1000

/** True when the task passes the status filter. */
export function matchesStatus(task: Task, status: MyWorkStatus, now = Date.now()): boolean {
  if (status === 'all') return true
  if (status === 'none') return !task.dueDate
  if (!task.dueDate) return false
  const due = new Date(task.dueDate).getTime()
  if (status === 'overdue') return due < now
  if (status === 'week') return due >= now && due <= now + 7 * DAY
  return true
}

/** True when the task matches the free-text search. */
export function matchesQuery(task: Task, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return `${task.title} ${task.key}`.toLowerCase().includes(q)
}

/** The My Work tasks in one order. Empty values sort last, like the board. */
export function sortMyTasks(
  tasks: MyWorkTask[],
  key: MyWorkSortKey = 'due',
  direction: 'asc' | 'desc' = 'asc',
): MyWorkTask[] {
  const sign = direction === 'asc' ? 1 : -1
  return [...tasks].sort((a, b) => {
    let cmp = 0
    switch (key) {
      case 'due':
        cmp = compareNullableDates(a.dueDate, b.dueDate)
        break
      case 'priority':
        cmp = PRIORITY_RANK[a.priority] - PRIORITY_RANK[b.priority]
        break
      case 'title':
        cmp = a.title.toLowerCase().localeCompare(b.title.toLowerCase())
        break
      case 'key':
        cmp = compareTaskKeys(a, b)
        break
      case 'updated':
        cmp = new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime()
        break
    }
    if (cmp !== 0) return cmp * sign
    // Same value — a stable, predictable order.
    return compareTaskKeys(a, b)
  })
}

/** A task without a due date sorts after the ones that have one. */
function compareNullableDates(left: string | null, right: string | null): number {
  if (!left && !right) return 0
  if (!left) return 1
  if (!right) return -1
  return new Date(left).getTime() - new Date(right).getTime()
}

/** One project inside a workspace, with the tasks of the current user. */
export interface MyWorkProjectGroup {
  projectId: string
  project: MyWorkProject | null
  tasks: MyWorkTask[]
}

/** One workspace, with its projects. */
export interface MyWorkWorkspaceGroup {
  workspaceId: string
  workspace: MyWorkWorkspace | null
  projects: MyWorkProjectGroup[]
}

/**
 * Group the tasks by workspace, then by project. The groups are ordered by
 * name; the tasks inside a group keep the order they came in with.
 */
export function groupMyTasks(tasks: MyWorkTask[]): MyWorkWorkspaceGroup[] {
  const workspaces = new Map<string, MyWorkWorkspaceGroup>()
  for (const task of tasks) {
    const workspaceId = task.workspace?.id ?? task.project?.workspaceId ?? 'unknown'
    let workspaceGroup = workspaces.get(workspaceId)
    if (!workspaceGroup) {
      workspaceGroup = { workspaceId, workspace: task.workspace, projects: [] }
      workspaces.set(workspaceId, workspaceGroup)
    }
    const projectId = task.project?.id ?? task.projectId
    let projectGroup = workspaceGroup.projects.find((p) => p.projectId === projectId)
    if (!projectGroup) {
      projectGroup = { projectId, project: task.project, tasks: [] }
      workspaceGroup.projects.push(projectGroup)
    }
    projectGroup.tasks.push(task)
  }
  const byName = (a: string, b: string) => a.localeCompare(b, undefined, { sensitivity: 'base' })
  const groups = [...workspaces.values()]
  groups.sort((a, b) =>
    byName(a.workspace?.name ?? '', b.workspace?.name ?? ''),
  )
  for (const group of groups) {
    group.projects.sort((a, b) => byName(a.project?.name ?? '', b.project?.name ?? ''))
  }
  return groups
}
